import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Mail, Phone, Calendar, Target, CheckCircle2, Clock, Loader2, AlertCircle, Users, RefreshCw, UserX, UserCheck, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@clareefai/ui';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import { FirstLoginChip, InvitedByLine } from '../../components/shared/ProfileMeta';
import AssignProspectsModal from '../../components/teams/AssignProspectsModal';
import AssignPromotersModal from '../../components/teams/AssignPromotersModal';
import EditTeamMemberModal from '../../components/teams/EditTeamMemberModal';
import { useGetTeamMemberByIdQuery, useAssignProspectsMutation, useAssignPromotersMutation, useUnassignPromotersMutation, useResendInvitationMutation, useReactivateTeamMemberMutation, useDeactivateTeamMemberMutation, useDeleteTeamMemberMutation } from '../../store/teamMemberApi';
import { useGetProspectsQuery } from '../../store/prospectApi';
import { useGetPromotersQuery } from '../../store/promoterApi';
import { useAbility } from '../../contexts/AbilityContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../utils/toast';

const TeamMemberDetail: React.FC = () => {
  const { t } = useTranslation();
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const ability = useAbility();
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isAssignPromotersModalOpen, setIsAssignPromotersModalOpen] = useState(false);
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Fetch team member details
  const { data: memberData, isLoading, isError, error, refetch } = useGetTeamMemberByIdQuery(memberId || '', {
    skip: !memberId,
  });

  // Fetch all prospects for assignment
  const { data: allProspects = [] } = useGetProspectsQuery();

  // Fetch all promoters for assignment
  const { data: allPromoters = [] } = useGetPromotersQuery();

  // Assign prospects mutation
  const [assignProspects, { isLoading: isAssigning }] = useAssignProspectsMutation();

  // Assign / unassign promoters mutations
  const [assignPromoters, { isLoading: isAssigningPromoters }] = useAssignPromotersMutation();
  const [unassignPromoters, { isLoading: isUnassigningPromoters }] = useUnassignPromotersMutation();

  // License management mutations
  const [resendInvitation, { isLoading: isResending }] = useResendInvitationMutation();
  const [reactivateTeamMember, { isLoading: isReactivating }] = useReactivateTeamMemberMutation();
  const [deactivateTeamMember, { isLoading: isDeactivating }] = useDeactivateTeamMemberMutation();
  const [deleteTeamMember, { isLoading: isDeleting }] = useDeleteTeamMemberMutation();

  const { user } = useAuth();
  const canManageMembers = ability.can('manage', 'TeamMember');
  const canEditMember = ability.can('update', 'TeamMember');
  const canDeleteMember = ability.can('delete', 'TeamMember');
  const isSelf = !!user?.teamMember?.id && user.teamMember.id === memberId;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-[#4339A0]" />
          <p className="text-gray-600">{t('teams.detail.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !memberData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="max-w-md w-full bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">{t('teams.detail.loadError')}</h3>
              <p className="text-sm text-red-700 mb-4">
                {(error as any)?.data?.message || t('teams.detail.loadErrorBody')}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => refetch()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t('teams.tryAgain')}
                </button>
                <button
                  onClick={() => navigate('/teams-sales')}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('teams.detail.backToTeams')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const member = memberData.teamMember;
  const assignedProspects = [...memberData.assignedProspects.active, ...memberData.assignedProspects.completed];
  const assignedPromoters = memberData.assignedPromoters ?? [];

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { label: string; className: string } } = {
      NEW: { label: t('teams.detail.statusNew'), className: 'bg-gray-100 text-gray-700 border-gray-300' },
      CONTACTED: { label: t('teams.detail.statusContacted'), className: 'bg-blue-100 text-blue-700 border-blue-300' },
      QUALIFIED: { label: t('teams.detail.statusQualified'), className: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
      PROPOSAL: { label: t('teams.detail.statusProposal'), className: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
      NEGOTIATION: { label: t('teams.detail.statusNegotiation'), className: 'bg-orange-100 text-orange-700 border-orange-300' },
      INTRODUCE: { label: t('teams.detail.statusIntroduce'), className: 'bg-purple-100 text-purple-700 border-purple-300' },
      WON: { label: t('teams.detail.statusWon'), className: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
      LOST: { label: t('teams.detail.statusLost'), className: 'bg-red-100 text-red-700 border-red-300' },
    };
    return statusMap[status?.toUpperCase()] || { label: status, className: 'bg-gray-100 text-gray-700 border-gray-300' };
  };

  const handleAssignProspects = async (selectedProspectIds: string[]) => {
    if (!memberId) return;
    try {
      await assignProspects({
        id: memberId,
        data: { prospectIds: selectedProspectIds },
      }).unwrap();
      toast.success(t('teams.detail.assignProspectsSuccess', { count: selectedProspectIds.length }));
      setIsAssignModalOpen(false);
      refetch();
    } catch (err: any) {
      console.error('Failed to assign prospects:', err);
      toast.error(err?.data?.message || t('teams.detail.assignProspectsError'));
    }
  };

  const handleAssignPromoters = async (selectedPromoterIds: string[]) => {
    if (!memberId) return;
    try {
      await assignPromoters({
        id: memberId,
        data: { promoterIds: selectedPromoterIds },
      }).unwrap();
      toast.success(t('teams.detail.assignPromotersSuccess', { count: selectedPromoterIds.length }));
      setIsAssignPromotersModalOpen(false);
      refetch();
    } catch (err: any) {
      console.error('Failed to assign promoters:', err);
      toast.error(err?.data?.message || t('teams.detail.assignPromotersError'));
    }
  };

  const handleUnassignPromoter = async (promoterId: string) => {
    if (!memberId) return;
    try {
      await unassignPromoters({
        id: memberId,
        data: { promoterIds: [promoterId] },
      }).unwrap();
      toast.success(t('teams.detail.unassignSuccess'));
      refetch();
    } catch (err: any) {
      console.error('Failed to unassign promoter:', err);
      toast.error(err?.data?.message || t('teams.detail.unassignError'));
    }
  };

  const handleResendInvitation = async () => {
    if (!memberId) return;
    try {
      await resendInvitation(memberId).unwrap();
      toast.success(t('teams.detail.resendSuccess'));
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.message || t('teams.detail.resendError'));
    }
  };

  const handleRevoke = async () => {
    if (!memberId) return;
    try {
      await deactivateTeamMember(memberId).unwrap();
      toast.success(t('teams.detail.revokeSuccess'));
      setIsRevokeDialogOpen(false);
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.message || t('teams.detail.revokeError'));
    }
  };

  const handleReactivate = async () => {
    if (!memberId) return;
    try {
      await reactivateTeamMember(memberId).unwrap();
      toast.success(t('teams.detail.reactivateSuccess'));
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.message || t('teams.detail.reactivateError'));
    }
  };

  const handleDelete = async () => {
    if (!memberId) return;
    try {
      await deleteTeamMember(memberId).unwrap();
      toast.success(t('teams.detail.deleteSuccess'));
      setIsDeleteDialogOpen(false);
      navigate('/teams-sales');
    } catch (err: any) {
      toast.error(err?.data?.message || t('teams.detail.deleteError'));
    }
  };

  const getMemberStatusBadge = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE': return { label: t('teams.detail.statusActive'), className: 'bg-green-100 text-green-800 border-green-300' };
      case 'PENDING': return { label: t('teams.detail.statusPending'), className: 'bg-amber-100 text-amber-800 border-amber-300' };
      case 'INACTIVE': return { label: t('teams.detail.statusInactive'), className: 'bg-red-100 text-red-800 border-red-300' };
      default: return { label: status, className: 'bg-gray-100 text-gray-700 border-gray-300' };
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="bg-gradient-to-r from-[#4339A0] via-[#5a4dba] to-[#3d2f8f] text-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <button
            onClick={() => navigate('/teams-sales')}
            className="mb-6 flex items-center space-x-2 text-[#b8b0f0] hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('teams.detail.backToTeams')}</span>
          </button>

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
            <div className="flex items-start space-x-6">
              <div className="relative">
                <Avatar
                  src={typeof member.avatar === 'string' && member.avatar.length > 2 ? member.avatar : undefined}
                  name={member.name}
                  size="lg"
                  className="w-32 h-32 border-4 border-white shadow-xl"
                />
              </div>

              <div>
                <h1 className="text-4xl font-bold mb-2">{member.name}</h1>
                <div className="flex items-center flex-wrap gap-2 mb-4">
                  <span className="px-4 py-1.5 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold rounded-full">
                    {member.role}
                  </span>
                  {(() => {
                    const badge = getMemberStatusBadge(member.status);
                    return (
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${badge.className}`}>
                        {badge.label}
                      </span>
                    );
                  })()}
                  <FirstLoginChip loggedIn={member.hasLoggedIn} />
                </div>

                <div className="space-y-2 mb-4">
                  <a href={`mailto:${member.email}`} className="flex items-center space-x-2 text-[#b8b0f0] hover:text-white transition-colors">
                    <Mail className="w-4 h-4" />
                    <span>{member.email}</span>
                  </a>
                  {member.phone && (
                    <a href={`tel:${member.phone}`} className="flex items-center space-x-2 text-[#b8b0f0] hover:text-white transition-colors">
                      <Phone className="w-4 h-4" />
                      <span>{member.phone}</span>
                    </a>
                  )}
                  <div className="flex items-center space-x-2 text-[#b8b0f0]">
                    <Calendar className="w-4 h-4" />
                    <span>{t('teams.detail.joined', { date: new Date(member.joinedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) })}</span>
                  </div>
                  {member.invitedBy && (
                    <InvitedByLine invitedBy={member.invitedBy} className="text-white pt-1" />
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:min-w-[400px]">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <Target className="w-8 h-8 text-[#9d8edb] mb-2" />
                <div className="text-3xl font-bold">{member.stats?.totalProspects ?? 0}</div>
                <div className="text-sm text-[#b8b0f0] mt-1">{t('teams.detail.totalProspects')}</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <Clock className="w-8 h-8 text-[#9d8edb] mb-2" />
                <div className="text-3xl font-bold">{member.stats?.activeProspects ?? 0}</div>
                <div className="text-sm text-[#b8b0f0] mt-1">{t('teams.detail.activeProspects')}</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <CheckCircle2 className="w-8 h-8 text-emerald-300 mb-2" />
                <div className="text-3xl font-bold">{member.stats?.convertedProspects ?? 0}</div>
                <div className="text-sm text-emerald-100 mt-1">{t('teams.detail.converted')}</div>
              </div>
            </div>
          </div>

          {/* Edit / Delete + License Management actions — hidden for self */}
          {(canManageMembers || canEditMember || canDeleteMember) && !isSelf && (
            <div className="mt-6 border-t border-white/20 pt-4 flex flex-wrap items-center gap-3">
              {canEditMember && (
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="flex items-center space-x-1.5 px-4 py-1.5 bg-white/20 hover:bg-white/30 text-white text-sm rounded-lg transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                  <span>{t('common.edit')}</span>
                </button>
              )}
              {canDeleteMember && (
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="flex items-center space-x-1.5 px-4 py-1.5 bg-red-600/80 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{t('common.delete')}</span>
                </button>
              )}
              <span className="text-white/40 mx-1">|</span>
              <span className="text-white/80 text-sm font-medium">{t('teams.detail.manageLicense')}</span>
              {member.status?.toUpperCase() === 'PENDING' && (
                <button
                  onClick={handleResendInvitation}
                  disabled={isResending}
                  className="flex items-center space-x-1.5 px-4 py-1.5 bg-white/20 hover:bg-white/30 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                >
                  {isResending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  <span>{t('teams.detail.resendInvite')}</span>
                </button>
              )}
              {member.status?.toUpperCase() === 'ACTIVE' && (
                <button
                  onClick={() => setIsRevokeDialogOpen(true)}
                  className="flex items-center space-x-1.5 px-4 py-1.5 bg-red-500/80 hover:bg-red-600 text-white text-sm rounded-lg transition-colors"
                >
                  <UserX className="w-4 h-4" />
                  <span>{t('teams.detail.revokeAccess')}</span>
                </button>
              )}
              {member.status?.toUpperCase() === 'INACTIVE' && (
                <button
                  onClick={handleReactivate}
                  disabled={isReactivating}
                  className="flex items-center space-x-1.5 px-4 py-1.5 bg-green-500/80 hover:bg-green-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                >
                  {isReactivating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                  <span>{t('teams.detail.reactivate')}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900">{t('teams.detail.assignedProspects')}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {t('teams.detail.assignedProspectsCount', { count: assignedProspects.length })}
              </p>
            </div>
            <button
              onClick={() => setIsAssignModalOpen(true)}
              disabled={isAssigning}
              className={`btn-primary flex items-center space-x-2 ${
                isAssigning ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isAssigning ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t('teams.detail.assigning')}</span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span>{t('teams.detail.assignProspects')}</span>
                </>
              )}
            </button>
          </div>

          <div className="p-6">
            {assignedProspects.length > 0 ? (
              <div className="space-y-3">
                {assignedProspects.map((prospect) => {
                  const statusInfo = getStatusBadge(prospect.status);
                  const prospectName = `${prospect.firstName ?? ''} ${prospect.lastName ?? ''}`.trim() || prospect.email;
                  return (
                    <div
                      key={prospect.id}
                      className="border-2 border-gray-200 rounded-lg p-5 hover:shadow-lg hover:border-blue-400 transition-all cursor-pointer bg-white"
                      onClick={() => navigate(`/prospect/${prospect.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h5 className="font-semibold text-gray-900">{prospectName}</h5>
                            <span className={`px-2 py-1 rounded-md text-xs font-medium border ${statusInfo.className}`}>
                              {statusInfo.label}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{prospect.company}</p>
                          <p className="text-sm text-gray-500 mt-1">{prospect.email}</p>
                        </div>
                        {prospect.createdAt && (
                          <div className="text-right">
                            <p className="text-xs text-gray-500">{t('teams.detail.created')}</p>
                            <p className="text-sm font-medium text-gray-700">
                              {new Date(prospect.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {t('teams.detail.noProspects')}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 bg-white rounded-xl shadow-sm">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900">{t('teams.detail.assignedPromoters')}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {t('teams.detail.assignedPromotersCount', { count: assignedPromoters.length })}
              </p>
            </div>
            <button
              onClick={() => setIsAssignPromotersModalOpen(true)}
              disabled={isAssigningPromoters}
              className={`btn-primary flex items-center space-x-2 ${
                isAssigningPromoters ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isAssigningPromoters ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t('teams.detail.assigning')}</span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span>{t('teams.detail.assignPromoters')}</span>
                </>
              )}
            </button>
          </div>
          <div className="p-6">
            {assignedPromoters.length > 0 ? (
              <div className="space-y-3">
                {assignedPromoters.map((promoter) => (
                    <div
                      key={promoter.id}
                      className="flex items-center justify-between border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-3">
                        <Users className="w-5 h-5 text-[#4339A0]" />
                        <div>
                          <p className="font-medium text-gray-900">{promoter.fullName}</p>
                          <p className="text-sm text-gray-500">{promoter.email}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleUnassignPromoter(promoter.id)}
                        disabled={isUnassigningPromoters}
                        className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                      >
                        {t('teams.detail.unassign')}
                      </button>
                    </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {t('teams.detail.noPromoters')}
              </div>
            )}
          </div>
        </div>
      </div>

      {isAssignModalOpen && (
        <AssignProspectsModal
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          onAssign={handleAssignProspects}
          teamMember={{
            id: member.id,
            name: member.name,
            role: member.role,
          }}
          allProspects={allProspects}
          assignedProspectIds={member.assignedProspects}
        />
      )}

      {isAssignPromotersModalOpen && (
        <AssignPromotersModal
          isOpen={isAssignPromotersModalOpen}
          onClose={() => setIsAssignPromotersModalOpen(false)}
          onAssign={handleAssignPromoters}
          teamMember={{
            id: member.id,
            name: member.name,
            role: member.role,
          }}
          allPromoters={allPromoters}
          assignedPromoterIds={assignedPromoters.map((p) => p.id)}
        />
      )}

      <ConfirmDialog
        isOpen={isRevokeDialogOpen}
        onClose={() => setIsRevokeDialogOpen(false)}
        onConfirm={handleRevoke}
        title={t('teams.detail.revokeDialogTitle')}
        description={t('teams.detail.revokeDialogBody', { name: member.name })}
        confirmLabel={t('teams.detail.revokeDialogConfirm')}
        variant="warning"
        isLoading={isDeactivating}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title={t('teams.detail.deleteDialogTitle')}
        description={t('teams.detail.deleteDialogBody', { name: member.name })}
        confirmLabel={t('teams.detail.deleteDialogConfirm')}
        variant="danger"
        isLoading={isDeleting}
      />

      {isEditModalOpen && (
        <EditTeamMemberModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            setIsEditModalOpen(false);
            refetch();
          }}
          member={{
            id: member.id,
            firstName: member.firstName,
            lastName: member.lastName,
            email: member.email,
            phone: member.phone,
            role: member.role,
          }}
        />
      )}
    </div>
  );
};

export default TeamMemberDetail;
